(function () {
  if (customElements.get('bloom-image-cta-slider')) return;

  class BloomImageCtaSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.currentIndex = 0;

      if (!this.track || !this.slides.length) return;

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index), { signal });
      });

      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
            const index = Number.parseInt(entry.target.dataset.slideIndex, 10);
            if (!Number.isNaN(index)) this.setCurrent(index);
          });
        }, { root: this.track, threshold: 0.5 });

        this.slides.forEach((slide) => this.observer.observe(slide));
      } else {
        this.track.addEventListener('scroll', () => this.syncFromScroll(), { signal, passive: true });
      }

      this.setCurrent(0);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.abortController?.abort();
      this.abortController = null;
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get smoothScroll() {
      return this.dataset.smoothScroll !== 'false' && !this.prefersReducedMotion;
    }

    normalizeIndex(index) {
      const count = this.slides.length;
      return ((index % count) + count) % count;
    }

    goTo(index, behavior) {
      if (!this.slides.length) return;
      const targetIndex = this.normalizeIndex(index);
      const slide = this.slides[targetIndex];
      const resolvedBehavior = behavior || (this.smoothScroll ? 'smooth' : 'auto');

      slide.scrollIntoView({ behavior: resolvedBehavior, inline: 'start', block: 'nearest' });
      this.setCurrent(targetIndex);
    }

    setCurrent(index) {
      this.currentIndex = this.normalizeIndex(index);

      this.slides.forEach((slide, slideIndex) => {
        slide.toggleAttribute('data-active', slideIndex === this.currentIndex);
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.currentIndex;
        dot.classList.toggle('bloom-image-cta-slider__bullet--active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    syncFromScroll() {
      const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.setCurrent(index);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const slide = event.target.closest('[data-slide-index]');
      if (!slide) return;
      const index = Number.parseInt(slide.dataset.slideIndex, 10);
      if (!Number.isNaN(index)) this.goTo(index, 'auto');
    }
  }

  customElements.define('bloom-image-cta-slider', BloomImageCtaSlider);
})();
